import React from "react";
import { Link } from "react-router-dom";
import moment from "moment";

/*
    MY SUBSCRIPTION STATUS — driven by GET /me/entitlement (backend#103).

    This component is the ONLY route to /main/cancel-membership anywhere in the
    app, which is why what it renders, and when, is a bigger deal than it
    looks.

    What it used to do, and why each part was wrong:

      - `if (user?.api_plan === "test") return;` suppressed the whole panel for
        free customers, so they could never cancel. It also returned undefined
        rather than null.
      - It read `cancel_at` from a live Stripe subscription, then printed a
        date from `user.api_active_until` — two sources, and the second is
        NULL until a customer's second payment, so the panel could render
        "access until Invalid date".
      - The cancel link lived in a single else-branch, so a customer with a
        scheduled plan change lost it entirely.
      - Its parent gated it on `user.stripe_customer_id`, which is blank for
        every free and comped customer and stale for anyone who paid since
        their last sign-in — the defect Dave reported on 2026-09-18.

    Now there is one input and one rule: render what `status` says, and show
    the cancel link exactly when the server says `canCancel`. The server sets
    that true only where a cancel would actually succeed, so the link can no
    longer lead to a page that fails at the customer.
*/

const formatDate = (iso) =>
  iso && moment(iso).isValid() ? moment(iso).format("MMMM DD, YYYY") : null;

const CancelLink = () => (
  <div className="py-3">
    <Link to="/main/cancel-membership" className="link-to fw--500">
      Cancel your Subscription
    </Link>
  </div>
);

const Reactivate = ({ children }) => (
  <>
    <p className="py-2 mb-0">{children}</p>
    <Link to="/api/plans" className="link-to">
      Reactivate your Subscription
    </Link>
  </>
);

const SubscriptionStatus = ({ entitlement }) => {
  const status = entitlement?.status;
  if (!status || status === "none") return null;

  const { canCancel, activeUntil, billing } = entitlement;
  const endsOn = formatDate(activeUntil);
  const renewsOn = formatDate(billing?.renewsOn);
  const nextPlan = billing?.nextPlan;

  if (status === "cancelling") {
    return (
      <>
        <h3>My Subscription Status</h3>
        <Reactivate>
          Your subscription is cancelled.
          {endsOn ? ` You have access until ${endsOn}` : ""}
        </Reactivate>
      </>
    );
  }

  if (status === "paused") {
    return (
      <>
        <h3>My Subscription Status</h3>
        <Reactivate>
          Your API access is paused. Start a plan again to switch it back on.
        </Reactivate>
      </>
    );
  }

  if (status === "ended") {
    return (
      <>
        <h3>My Subscription Status</h3>
        <Reactivate>Your plan has ended.</Reactivate>
      </>
    );
  }

  if (status === "unsupported") {
    // A real, often paying customer on a plan this system has no rule for.
    // Nothing they can do in the UI will fix it, so point at a human rather
    // than at the Plans page.
    return (
      <>
        <h3>My Subscription Status</h3>
        <p className="py-2 mb-0">
          We need to sort your plan out by hand. Please contact{" "}
          <a href="mailto:apisupport@golfbert.com" className="link-to">
            apisupport@golfbert.com
          </a>
          .
        </p>
      </>
    );
  }

  // active
  return (
    <div className="mt-5">
      <h5>My Subscription Status</h5>
      {nextPlan ? (
        <span>
          Your subscription will remain active until {renewsOn}. After that,
          your plan will automatically switch to {nextPlan}
        </span>
      ) : renewsOn ? (
        <span>
          Your subscription is scheduled to renew automatically on {renewsOn}
        </span>
      ) : (
        <span>Your plan is active.</span>
      )}
      {/*
          The cancel link renders in EVERY active state now — including a
          scheduled plan change, and including the free plan, which never had
          a way out at all. #84 tried to fix this in the component and was
          correctly reverted (#85), because the parent's gate made the
          component unreachable for the customers it was written for. The gate
          is gone; this is the other half.
      */}
      {canCancel && <CancelLink />}
    </div>
  );
};

export default SubscriptionStatus;
